// https://boost.allnetru.dev/api/documentation/organizer#/
import { createApi } from '@reduxjs/toolkit/query/react';
import {
  PatchOrganizerLandingStatusByIdResponse,
  GetOrganizerCredentialsResponse,
  GetOrganizerCredentialsRequest,
  GetOrganizerCredentialsByEventIdRequest,
  AdAccountValue,
} from './models';
import { INIT_CURRENT_PAGE } from 'lib.shared/src/constants';
import { API_EVENT_TAGS } from 'lib.store/src/tags';
import { baseQueryWithReauth } from '../../main/baseQueryWithReauth';

export const adOrganizerCredentials = createApi({
  reducerPath: API_EVENT_TAGS.ORGANIZER_CREDENTIALS,
  baseQuery: baseQueryWithReauth,
  tagTypes: [API_EVENT_TAGS.ORGANIZER_CREDENTIALS],
  endpoints: (build) => ({
    getOrganizerCredentials: build.query<
      GetOrganizerCredentialsResponse,
      GetOrganizerCredentialsRequest | void
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();
        params?.filters?.statuses?.forEach((status) =>
          searchParams.append('filters[statuses][]', String(status)),
        );
        params?.filters?.channels?.forEach((channel) =>
          searchParams.append('filters[channels][]', String(channel)),
        );
        if (params?.page) {
          searchParams.append('page', String(params.page));
        }
        const queryString = searchParams.toString();
        return {
          url: `/credentials${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_CREDENTIALS],
    }),
    patchOrganizerCredentialsById: build.mutation<
      PatchOrganizerLandingStatusByIdResponse,
      { credentialId: number; selections: Record<string, { value: AdAccountValue }> }
    >({
      query: ({ credentialId, selections }) => {
        return {
          url: `/credentials/${credentialId}`,
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: selections,
        };
      },
    }),
    postOrganizerEventCredentialsById: build.mutation<
      PatchOrganizerLandingStatusByIdResponse,
      { eventId: string; credentialId: number }
    >({
      query: ({ eventId, credentialId }) => {
        return {
          url: `/organizer/events/${eventId}/setup/credentials/${credentialId}`,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          providesTags: [API_EVENT_TAGS.ORGANIZER_CREDENTIALS],
        };
      },
    }),

    patchOrganizerCredentialsStatus: build.mutation<
      PatchOrganizerLandingStatusByIdResponse,
      { credentialId: number; statusId: number }
    >({
      query: ({ credentialId, statusId }) => {
        return {
          url: `/credentials/${credentialId}/status/${statusId}`,
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          providesTags: [API_EVENT_TAGS.ORGANIZER_CREDENTIALS],
        };
      },
    }),
    getOrganizerCredentialsByEventId: build.query<
      GetOrganizerCredentialsResponse,
      GetOrganizerCredentialsByEventIdRequest
    >({
      query: ({ eventId, filters, page = INIT_CURRENT_PAGE }) => {
        const searchParams = new URLSearchParams();
        filters?.statuses?.forEach((status) =>
          searchParams.append('filters[statuses][]', String(status)),
        );
        filters?.channels?.forEach((channel) =>
          searchParams.append('filters[channels][]', String(channel)),
        );
        if (page) {
          searchParams.append('page', String(page));
        }
        const queryString = searchParams.toString();
        return {
          url: `/organizer/events/${eventId}/setup/credentials${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_CREDENTIALS],
    }),
  }),
});

export const {
  useGetOrganizerCredentialsQuery,
  useGetOrganizerCredentialsByEventIdQuery,
  usePatchOrganizerCredentialsByIdMutation,
  usePostOrganizerEventCredentialsByIdMutation,
  usePatchOrganizerCredentialsStatusMutation,
} = adOrganizerCredentials;
